import { cva } from 'class-variance-authority';
import { transitionAll } from '@/lib/styleUtils';

const containerBase = [
  'tw:flex tw:items-center tw:gap-[var(--spacing-micro)]',
  'tw:rounded-[var(--radius-8)] tw:border tw:border-solid',
  'tw:p-[var(--spacing-small)] tw:min-h-10',
  transitionAll,
].join(' ');

export const baseInputContainerVariants = cva(containerBase, {
  variants: {
    hasError: {
      false: [
        'tw:bg-gray-lighter tw:border-gray-lighter',
        'tw:hover:border-primary tw:hover:bg-white',
        'tw:focus-within:border-primary tw:focus-within:bg-white tw:focus-within:shadow-[0_0_0_var(--spacing-nano,4px)_color-mix(in_srgb,var(--tw-color-primary)_20%,transparent)]',
      ].join(' '),
      true: [
        'tw:bg-gray-lighter tw:border-error',
        'tw:hover:border-error tw:hover:bg-white tw:hover:shadow-[0_0_0_var(--spacing-nano,4px)_color-mix(in_srgb,var(--tw-color-error),80%_transparent)]',
        'tw:focus-within:border-error tw:focus-within:bg-white tw:focus-within:shadow-[0_0_0_var(--spacing-nano,4px)_color-mix(in_srgb,var(--tw-color-error),80%_transparent)]',
      ].join(' '),
    },
    isDisabled: {
      true: [
        'tw:bg-gray-lighter tw:border-gray-lighter tw:text-gray',
        'tw:hover:bg-gray-lighter tw:hover:border-gray-lighter tw:hover:shadow-none',
        'tw:focus-within:bg-gray-lighter tw:focus-within:border-gray-lighter tw:focus-within:shadow-none',
      ].join(' '),
      false: '',
    },
  },
  defaultVariants: {
    hasError: false,
    isDisabled: false,
  },
});

/** Base classes for the inner <input> element (layout, appearance, no border). */
export const inputBaseClasses = [
  'tw:min-w-0 tw:flex-1',
  'tw:bg-transparent',
  'tw:border-0 tw:outline-none',
  // 16px on all viewports per the design system; also prevents iOS/Android focus auto-zoom (PB-2687).
  'tw:text-16 tw:text-gray-darker placeholder:tw:text-gray-light',
  'tw:disabled:text-gray tw:disabled:[-webkit-text-fill-color:var(--tw-color-gray)] tw:disabled:cursor-default',
].join(' ');

/** Wrapper for the left icon inside the input container. */
export const leftIconWrapperClasses =
  'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:max-h-5 tw:text-gray-darker [&>svg]:tw:max-h-5 [&>svg]:tw:w-auto';

/** Wrapper for the right-side actions (clear button, right button, right icon). */
export const rightActionsWrapperClasses =
  'tw:flex tw:shrink-0 tw:items-center tw:gap-[2px] tw:max-h-5 tw:text-gray-darker [&>svg]:tw:max-h-5 [&>svg]:tw:w-auto [&>button]:tw:max-h-5';

/** Clear button that appears when there is a value and onClear is provided. */
export const clearButtonClasses =
  'tw:flex tw:items-center tw:justify-center tw:rounded tw:p-1 tw:transition-opacity hover:tw:opacity-80 focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-primary';

/** Optional small action button rendered on the right side of the input. */
export const rightActionButtonClasses =
  'tw:rounded tw:p-0 tw:text-12 tw:font-medium tw:text-primary tw:transition-opacity hover:tw:opacity-90 focus:tw:outline-none focus-visible:tw:ring-2 focus-visible:tw:ring-primary';

/** Wrapper for the right-side icon. */
export const rightIconWrapperClasses =
  'tw:flex tw:items-center tw:text-gray-dark tw:max-h-5 [&>svg]:tw:max-h-5 [&>svg]:tw:w-auto';
